import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, getTestTokens, setAuthToken, User, LoginResponse } from '../../services/api';

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await login(email, password);
    setAuthToken(response.token);
    return response;
  }
);

export const loginWithTestToken = createAsyncThunk(
  'auth/loginWithTestToken',
  async (role: 'coo' | 'finance') => {
    const tokens = await getTestTokens();
    const tokenData = role === 'coo' ? tokens.coo : tokens.finance;
    setAuthToken(tokenData.token);
    return { token: tokenData.token, user: tokenData.user };
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      setAuthToken(null);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loginUser
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // loginWithTestToken
      .addCase(loginWithTestToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithTestToken.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(loginWithTestToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get test token';
      });
  },
});

export const { setCredentials, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
